import * as React from 'react'
import $ from 'jquery'
import AppHeader from '../../../../components/AppView/Header'
import Endpoints from '../../../../constants/Endpoints'
import moment from 'moment'
import { message, Skeleton, Alert } from 'antd'
import { inject, observer } from 'mobx-react'
import { AppView } from '../../../../components/AppView'
import { IInvoice, API, Supplier, ILocation } from '@getgreenline/homi-shared'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { browserHistory } from 'react-router'
import { Routes } from '../../../../constants/Routes'
import { PrintingStore } from '../../Printing/v2/PrintingStore'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import { GrPrice } from '../../../../utilities/helpers'
import Helmet from 'react-helmet'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  params: {
    purchaseOrderId: string
  }
}

interface State {
  invoice: IInvoice | undefined
  vendor?: Supplier
  location?: ILocation
}

const PRINTABLE_HTML_ID = 'print-area'

@inject('currentCompanyStore')
@observer
export class PrintInvoice extends React.Component<Props, State> {
  printingStore = new PrintingStore()

  constructor(props: Props) {
    super(props)
    this.state = {
      invoice: undefined,
    }
  }

  componentDidMount() {
    this.fetchInvoice()
  }

  async fetchInvoice() {
    const company = this.props.currentCompanyStore!.company!
    const locations = this.props.currentCompanyStore!.locations || []
    const invoiceId = this.props.params.purchaseOrderId
    try {
      const fetchedInvoice = await API.getInvoiceById(company.id, invoiceId)
      const location = locations.find((location) => location.id === fetchedInvoice.locationId)
      let vendor: Supplier | undefined
      if (fetchedInvoice.supplierId) {
        const allVendors = await API.getSuppliers(company.id)
        vendor = allVendors.find((vendor) => vendor.id === fetchedInvoice.supplierId)
      }
      this.setState({
        invoice: fetchedInvoice,
        location: location,
        vendor: vendor,
      })
    } catch (error) {
      message.error(`There was an error fetching purchase order ${invoiceId}`)
    }
  }

  print() {
    const html = `
      <html>
        <style>
          @media print
          {
            table { page-break-after:auto }
            tr    { page-break-inside:avoid; page-break-after:auto }
            td    { page-break-inside:avoid; page-break-after:auto }
            thead { display:table-header-group }
            tfoot { display:table-footer-group }
          }
        </style>
        <body>
          ${$('#' + PRINTABLE_HTML_ID).html()}
        </body>
      </html>
    `

    // Download labels in a new tab as a post request
    // Reference: https://stackoverflow.com/questions/178964/javascript-post-on-form-submit-open-a-new-window
    const form = document.createElement('form')
    form.setAttribute('method', 'post')
    form.setAttribute('action', Endpoints.PRINTING_API_URL + Endpoints.GET_PDF_LABEL_V2)

    // HTML
    const htmlField = document.createElement('input')
    htmlField.setAttribute('name', 'html')
    htmlField.setAttribute('value', html)
    form.appendChild(htmlField)

    // Page width
    const pageWidthField = document.createElement('input')
    pageWidthField.setAttribute('type', 'number')
    pageWidthField.setAttribute('name', 'pageWidth')
    pageWidthField.setAttribute('value', '8.27')
    form.appendChild(pageWidthField)

    // Page height
    const pageHeightField = document.createElement('input')
    pageHeightField.setAttribute('type', 'number')
    pageHeightField.setAttribute('name', 'pageHeight')
    pageHeightField.setAttribute('value', '11.69')
    form.appendChild(pageHeightField)

    document.body.appendChild(form)
    form.submit()
    form!.parentNode!.removeChild(form)
  }

  render() {
    const company = this.props.currentCompanyStore!.company!
    const invoiceId = this.props.params.purchaseOrderId
    const { invoice, location, vendor } = this.state

    const orderDate = invoice ? invoice.orderDate : undefined

    return (
      <AppView column>
        <Helmet>
          <title>Purchase Orders | Edit | Print</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <AppHeader
          title={`Print PDF`}
          backVisible={true}
          onBackClick={() =>
            browserHistory.push({
              pathname: Routes.EDIT_INVOICE(company.id, invoiceId),
            })
          }
        >
          <BlazeButton
            buttonProps={{
              type: 'primary',
              icon: 'container',
              onClick: () => this.print(),
            }}
            titleProps={{
              title: 'Download PDF',
            }}
          />
        </AppHeader>
        <AppView column>
          {!invoice ? (
            <div className='container'>
              <Skeleton active />
            </div>
          ) : (
            <div className='container'>
              <br />

              <Alert showIcon message='Optimized for A4 paper' style={{ width: 200 }} />

              <br />

              <div
                className='border border-secondary'
                id={PRINTABLE_HTML_ID}
                style={{ width: '8.25in' }}
              >
                <div style={{ padding: '0.5in', fontSize: '85%' }}>
                  {/* Header section */}
                  <h1 style={{ marginBottom: 0 }}>{company.name}</h1>
                  <div>Purchase order: #{invoice.customId || invoice.id}</div>
                  {!!orderDate && <div>Ordered: {moment(orderDate).format('MMM DD YYYY')}</div>}
                  <hr />

                  {/* 2-column vendor/location section */}
                  <div style={{ display: 'flex' }}>
                    <div style={{ width: '50%' }}>
                      <div>
                        <b>Vendor</b>
                      </div>
                      {!!vendor && (
                        <>
                          <div>{vendor.name}</div>
                          <div>{vendor.address}</div>
                        </>
                      )}
                      <div></div>
                    </div>
                    <div style={{ width: '50%' }}>
                      <div>
                        <b>Ship to</b>
                      </div>
                      {!!location && (
                        <>
                          <div>{location.name}</div>
                          <div>{location.address}</div>
                        </>
                      )}
                    </div>
                  </div>
                  <hr />

                  {/* Purchase order table */}
                  <table style={{ width: '100%', fontSize: '85%' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left' }}>Product</th>
                        <th style={{ textAlign: 'left' }}>SKU</th>
                        <th style={{ textAlign: 'right' }}>Quantity</th>
                        <th style={{ textAlign: 'right' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.lines.map((line) => {
                        return (
                          <tr key={line.id}>
                            <td style={{ paddingRight: 5 }}>{line.productName}</td>
                            <td style={{ paddingRight: 5 }}>{line.sku}</td>
                            <td style={{ textAlign: 'right', paddingRight: 5 }}>{line.quantity}</td>
                            <td style={{ textAlign: 'right' }}>
                              {GrPrice.convertCentToDollar(line.total)}
                            </td>
                          </tr>
                        )
                      })}
                      <tr>
                        <td colSpan={4}>
                          <hr />
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={3} style={{ textAlign: 'right', paddingRight: 5 }}>
                          Subtotal
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {GrPrice.convertCentToDollar(invoice.subTotal)}
                        </td>
                      </tr>
                      {invoice.fees.map((fee) => {
                        return (
                          <tr key={fee.id}>
                            <td colSpan={3} style={{ textAlign: 'right', paddingRight: 5 }}>
                              {fee.name}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              {GrPrice.convertCentToDollar(fee.total)}
                            </td>
                          </tr>
                        )
                      })}
                      {invoice.groupedTaxes.map((tax) => {
                        return (
                          <tr key={tax.taxId}>
                            <td colSpan={3} style={{ textAlign: 'right', paddingRight: 5 }}>
                              {tax.taxName}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              {GrPrice.convertCentToDollar(tax.totalAmount)}
                            </td>
                          </tr>
                        )
                      })}
                      <tr>
                        <td colSpan={3} style={{ textAlign: 'right', paddingRight: 5 }}>
                          <b>Total</b>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {GrPrice.convertCentToDollar(invoice.total)}
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  {!!invoice.notes && (
                    <>
                      <hr />
                      <div>
                        <b>Notes</b>
                      </div>
                      {invoice.notes}
                    </>
                  )}
                </div>
              </div>

              <br />
              <br />
            </div>
          )}
        </AppView>
      </AppView>
    )
  }
}
